import { useEffect, useState } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, deleteDoc, getDocs, updateDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';


const STATUS_STYLES = {
  pending:   'bg-amber-light text-[#92400E]',
  confirmed: 'bg-green-light text-[#065F46]',
  declined:  'bg-red-light text-dr',
};

const STATUS_LABELS = {
  pending:   'Pending',
  confirmed: 'Confirmed',
  declined:  'Declined',
};

const emptyForm = {
  supplierId: '',
  serviceTypes: [],
  staffNotes: '',
};

// Returns service types for a given assignment, handling both old (string) and new (array) formats
function getServiceTypes(a) {
  if (Array.isArray(a.serviceTypes) && a.serviceTypes.length) return a.serviceTypes;
  if (a.serviceType) return [a.serviceType];
  return [];
}

export default function SupplierAssignments({ requestId, request }) {
  const [assignments, setAssignments] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [allServiceTypes, setAllServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [manualApprovingId, setManualApprovingId] = useState(null);

  // Load assignments for this request
  useEffect(() => {
    const q = query(
      collection(db, 'bookingSuppliers'),
      where('requestId', '==', requestId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0));
      setAssignments(docs);
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  // Load supplier directory
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'suppliers'), (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setDirectory(docs);
    });
    return unsub;
  }, []);

  // Load global supplier categories as fallback when a supplier has no types defined
  useEffect(() => {
    const q = query(collection(db, 'supplierCategories'), orderBy('order', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setAllServiceTypes(
        snap.docs
          .map((d) => d.data())
          .filter((c) => c.active !== false)
          .map((c) => c.name)
      );
    });
    return unsub;
  }, []);

  const selectedSupplier = directory.find((s) => s.id === form.supplierId) || null;
  const availableServiceTypes = selectedSupplier?.serviceTypes?.length
    ? selectedSupplier.serviceTypes
    : allServiceTypes;

  function handleSupplierChange(e) {
    // Reset service types when supplier changes
    setForm((prev) => ({ ...prev, supplierId: e.target.value, serviceTypes: [] }));
  }

  function handleServiceTypeToggle(type) {
    setForm((prev) => {
      const has = prev.serviceTypes.includes(type);
      return {
        ...prev,
        serviceTypes: has
          ? prev.serviceTypes.filter((t) => t !== type)
          : [...prev.serviceTypes, type],
      };
    });
  }

  async function handleManualApprove(assignment) {
    if (!window.confirm(`Mark ${assignment.supplierName} as confirmed? Use this when the supplier has approved verbally or by phone.`)) return;
    setManualApprovingId(assignment.id);
    try {
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        status: 'confirmed',
        manuallyApproved: true,
        manuallyApprovedAt: serverTimestamp(),
        respondedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      alert('Failed to update. Please try again.');
    } finally {
      setManualApprovingId(null);
    }
  }

  async function handleDelete(assignment) {
    const types = getServiceTypes(assignment).join(', ') || 'this service';
    if (!window.confirm(`Remove ${assignment.supplierName} (${types}) from this tour? This will also delete any associated invoice.`)) return;
    setDeletingId(assignment.id);
    try {
      // Delete invoice file from Storage if one was uploaded
      if (assignment.invoiceUrl && assignment.invoiceFileName) {
        try {
          const path = `invoices/${requestId}/${assignment.id}/${assignment.invoiceFileName}`;
          await deleteObject(storageRef(storage, path));
        } catch {
          // File may already be gone — not fatal
        }
      }
      // Clear linkedSupplierAssignmentId from any linked expense
      const linkedExpenses = await getDocs(
        query(collection(db, 'expenses'), where('linkedSupplierAssignmentId', '==', assignment.id))
      );
      await Promise.all(
        linkedExpenses.docs.map((d) => updateDoc(d.ref, { linkedSupplierAssignmentId: null }))
      );
      // Delete the assignment itself
      await deleteDoc(doc(db, 'bookingSuppliers', assignment.id));
    } catch (err) {
      console.error(err);
      alert('Failed to delete supplier. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleAssign(e) {
    e.preventDefault();
    setError('');
    if (!form.supplierId || form.serviceTypes.length === 0) {
      setError('Please select a supplier and at least one service type.');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'bookingSuppliers'), {
        requestId,
        sport: request.sport,
        destination: request.destination,
        departureDate: request.departureDate,
        returnDate: request.returnDate,
        participants: request.participants || null,
        supplierName: selectedSupplier.name,
        supplierEmail: selectedSupplier.email,
        supplierId: selectedSupplier.id,
        serviceTypes: form.serviceTypes,
        staffNotes: form.staffNotes.trim(),
        status: 'pending',
        supplierNotes: '',
        respondedAt: null,
        createdAt: serverTimestamp(),
      });
      setForm(emptyForm);
      setShowForm(false);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Suppliers</h3>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-[12px] font-medium text-pb hover:text-navy border border-pb/20 hover:border-pb/40 px-3 py-1.5 rounded-[8px] transition"
          >
            + Assign supplier
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : (
        <>
          {assignments.length === 0 && !showForm && (
            <p className="text-[13px] text-[#9CA3AF]">No suppliers assigned yet.</p>
          )}

          {assignments.length > 0 && (
            <div className="space-y-3 mb-4">
              {assignments.map((a) => {
                const types = getServiceTypes(a);
                const isPending = a.status === 'pending';
                return (
                  <div key={a.id} className="flex items-start justify-between gap-3 p-3 rounded-[8px] bg-surface border border-[#F3F4F6]">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <p className="text-[13px] font-medium text-[#111827]">{a.supplierName}</p>
                        {types.length > 0 && (
                          <>
                            <span className="text-[12px] text-[#9CA3AF]">·</span>
                            <div className="flex flex-wrap gap-1">
                              {types.map((t) => (
                                <span key={t} className="text-[11px] font-medium bg-[#EEF2FF] text-[#4338CA] px-2 py-0.5 rounded-full">{t}</span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <p className="text-[12px] text-[#9CA3AF]">{a.supplierEmail}</p>
                      {a.manuallyApproved && (
                        <p className="text-[11px] text-[#6B7280] mt-0.5 italic">Approved manually (phone/verbal)</p>
                      )}
                      {a.supplierNotes && (
                        <p className="text-[12px] text-[#6B7280] mt-1 italic">"{a.supplierNotes}"</p>
                      )}
                      {a.staffNotes && (
                        <p className="text-[12px] text-[#9CA3AF] mt-1">Note: {a.staffNotes}</p>
                      )}
                      {a.invoiceUrl ? (
                        <a
                          href={a.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-semibold text-pb hover:text-navy transition"
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          </svg>
                          {a.invoiceFileName || 'Invoice'} ↗
                        </a>
                      ) : (
                        <p className="text-[11.5px] text-[#D1D5DB] mt-1.5">No invoice uploaded</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] whitespace-nowrap ${STATUS_STYLES[a.status] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                        {STATUS_LABELS[a.status] || a.status}
                      </span>
                      {isPending && (
                        <button
                          onClick={() => handleManualApprove(a)}
                          disabled={manualApprovingId === a.id}
                          title="Mark as confirmed — use when supplier approved by phone"
                          className="text-[11px] font-medium text-[#059669] hover:text-[#047857] border border-[#A7F3D0] hover:border-[#6EE7B7] px-2 py-1 rounded-[6px] transition disabled:opacity-40 whitespace-nowrap"
                        >
                          {manualApprovingId === a.id ? '…' : '✓ Phone approved'}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(a)}
                        disabled={deletingId === a.id}
                        title="Remove supplier"
                        className="p-1 text-[#D1D5DB] hover:text-dr transition disabled:opacity-40"
                      >
                        {deletingId === a.id ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showForm && (
        <form onSubmit={handleAssign} className="space-y-3 border-t border-[#F3F4F6] pt-4 mt-2">

          {directory.length === 0 ? (
            <p className="text-[12px] text-[#6B7280]">
              No suppliers in your directory yet.{' '}
              <a href="/staff/suppliers" className="text-pb hover:underline">Add suppliers</a>{' '}
              first, then assign them here.
            </p>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">
                  Supplier <span className="text-dr">*</span>
                </label>
                <select
                  value={form.supplierId}
                  onChange={handleSupplierChange}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                >
                  <option value="">Select a supplier</option>
                  {directory.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} — {s.email}
                    </option>
                  ))}
                </select>
              </div>

              {form.supplierId && (
                <div>
                  <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">
                    Services requested <span className="text-dr">*</span>
                  </label>
                  {availableServiceTypes.length === 0 ? (
                    <p className="text-[12px] text-[#9CA3AF]">No service types available for this supplier.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {availableServiceTypes.map((type) => {
                        const selected = form.serviceTypes.includes(type);
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => handleServiceTypeToggle(type)}
                            className={`text-[12px] font-medium px-3 py-1.5 rounded-[8px] border transition ${
                              selected
                                ? 'bg-pb text-white border-pb'
                                : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-pb/40'
                            }`}
                          >
                            {type}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {form.serviceTypes.length > 0 && (
                    <p className="text-[11px] text-[#6B7280] mt-1.5">
                      Selected: {form.serviceTypes.join(', ')}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-[12px] font-medium text-[#6B7280] mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={form.staffNotes}
                  onChange={(e) => setForm((p) => ({ ...p, staffNotes: e.target.value }))}
                  placeholder="e.g. 3 nights, twin rooms"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>

              <p className="text-[11px] text-[#9CA3AF]">
                The supplier will receive an email asking them to confirm their availability between the tour dates. Specific times will be sent once the itinerary is built.
              </p>
            </div>
          )}

          {error && <p className="text-[12px] text-dr">{error}</p>}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving || directory.length === 0}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Assign & notify supplier'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setForm(emptyForm); setError(''); }}
              className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
